<template>
  <div class="drama-category-management">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>短剧分类管理</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="handleAdd">
          <i class="el-icon-plus"></i> 新增分类
        </el-button>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar" style="margin-bottom: 20px;">
        <el-input
          v-model="searchKeyword"
          placeholder="请输入分类名称搜索"
          prefix-icon="el-icon-search"
          clearable
          style="width: 300px;"
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
        >
        </el-input>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch" style="margin-left: 10px;">搜索</el-button>
        <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="categoryList"
        border
        stripe
        v-loading="loading"
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column prop="name" label="分类名称" min-width="150"></el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="100" align="center"></el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180" align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        style="margin-top: 20px; text-align: right;"
      >
      </el-pagination>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      @close="handleDialogClose"
    >
      <el-form ref="categoryForm" :model="categoryForm" :rules="categoryRules" label-width="100px">
        <el-form-item label="分类名称" prop="name">
          <el-input 
            v-model="categoryForm.name" 
            placeholder="请输入分类名称(必填)"
            maxlength="50"
            show-word-limit
          ></el-input>
          <div class="form-tip" style="color: #909399; font-size: 12px; margin-top: 5px;">
            ℹ️ 系统将自动去除首尾空格,并检查重复
          </div>
        </el-form-item>
        
        <el-form-item label="排序权重" prop="sort">
          <el-input-number 
            v-model="categoryForm.sort" 
            :min="0" 
            :max="9999" 
            style="width: 100%;"
            placeholder="选填,不填则自动递增"
          ></el-input-number>
        </el-form-item>
        
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="categoryForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { request } from '@/utils/http.js'

export default {
  name: 'DramaCategoryManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      searchKeyword: '',
      categoryList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增分类',
      categoryForm: {
        id: null,
        name: '',
        sort: null,
        status: 1
      },
      categoryRules: {
        name: [
          { required: true, message: '请输入分类名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.loadCategoryList()
  },
  methods: {
    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      // 将 ISO 格式时间转换为更易读的格式
      // 例如: 2026-05-18T17:10:14 -> 2026-05-18 17:10:14
      return dateTime.replace('T', ' ')
    },
    
    // 加载分类列表
    async loadCategoryList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        const response = await request('/admin/drama/category/list', { 
          method: 'GET',
          params 
        })
        
        if (response.code === 200 && response.data) {
          // 后端返回的是 MyBatis-Plus Page 对象
          this.categoryList = response.data.records || []
          this.pagination.total = response.data.total || 0
        } else {
          this.$message.error(response.msg || '加载失败')
        }
      } catch (error) {
        this.$message.error('加载分类列表失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.loadCategoryList()
    },

    // 重置
    handleReset() {
      this.searchKeyword = ''
      this.pagination.page = 1
      this.loadCategoryList()
    },

    // 新增
    handleAdd() {
      this.dialogTitle = '新增分类'
      this.categoryForm = {
        id: null,
        name: '',
        sort: null,       // 排序设为null,让后端自动分配
        status: 1         // 默认启用
      }
      this.dialogVisible = true
    },

    // 编辑
    handleEdit(row) {
      this.dialogTitle = '编辑分类'
      this.categoryForm = {
        id: row.id,
        name: row.name,
        sort: row.sortOrder,  // 使用sortOrder
        status: row.status
      }
      this.dialogVisible = true
    },

    // 删除
    handleDelete(row) {
      this.$confirm(`确定要删除分类"${row.name}"吗?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await request(`/admin/drama/category/delete/${row.id}`, { 
            method: 'DELETE' 
          })
          
          if (response.code === 200) {
            this.$message.success('删除成功')
            this.loadCategoryList()
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败: ' + (error.message || '未知错误'))
        }
      }).catch(() => {})
    },

    // 提交表单
    handleSubmit() {
      this.$refs.categoryForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const url = this.categoryForm.id 
              ? '/admin/drama/category/update' 
              : '/admin/drama/category/add'
            const method = this.categoryForm.id ? 'PUT' : 'POST'
            
            // 构建提交数据
            const submitData = {
              name: this.categoryForm.name.trim(), // 去除首尾空格
            }
            
            // 只在有值时才添加可选字段(让后端使用默认值)
            if (this.categoryForm.sort !== null && this.categoryForm.sort !== undefined) {
              submitData.sortOrder = this.categoryForm.sort
            }
            
            if (this.categoryForm.status !== null && this.categoryForm.status !== undefined) {
              submitData.status = this.categoryForm.status
            }
            
            // 编辑时需要ID
            if (this.categoryForm.id) {
              submitData.id = this.categoryForm.id
            }
            
            console.log('提交数据:', submitData)
            
            const response = await request(url, { 
              method,
              body: JSON.stringify(submitData)
            })
            
            if (response.code === 200) {
              this.$message.success(this.categoryForm.id ? '更新成功' : '新增成功')
              this.dialogVisible = false
              this.loadCategoryList()
            } else {
              this.$message.error(response.msg || '操作失败')
            }
          } catch (error) {
            this.$message.error('操作失败: ' + (error.message || '未知错误'))
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 关闭对话框
    handleDialogClose() {
      this.$refs.categoryForm.resetFields()
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.loadCategoryList()
    },

    // 页码改变
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadCategoryList()
    }
  }
}
</script>

<style scoped>
.drama-category-management {
  padding: 20px;
}

.box-card {
  border-radius: 8px;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}

.search-bar {
  display: flex;
  align-items: center;
}
</style>
